/**
 * RoleBadge Component
 * 
 * Color-coded badge for displaying user roles.
 * Supports optional tooltips with role descriptions.
 */

import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { type AppRole, roleDescriptions } from '@/lib/permissions';

interface RoleBadgeProps {
  role: AppRole | string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showTooltip?: boolean;
}

const roleStyles: Record<string, { bg: string; text: string; label: string }> = {
  firm_owner: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-300',
    label: 'Firm Owner',
  },
  attorney: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    label: 'Attorney',
  },
  paralegal: {
    bg: 'bg-teal-100 dark:bg-teal-900/30',
    text: 'text-teal-700 dark:text-teal-300',
    label: 'Paralegal',
  },
  support: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-700 dark:text-gray-300',
    label: 'Support',
  },
  owner: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-300',
    label: 'Owner',
  },
  member: {
    bg: 'bg-sky-100 dark:bg-sky-900/30',
    text: 'text-sky-700 dark:text-sky-300',
    label: 'Member',
  },
  super_admin: {
    bg: 'bg-rose-100 dark:bg-rose-900/30',
    text: 'text-rose-700 dark:text-rose-300',
    label: 'Super Admin',
  },
};

const sizeStyles = {
  sm: 'text-[10px] px-1.5 py-0',
  md: 'text-xs px-2 py-0.5',
  lg: 'text-sm px-2.5 py-1',
};

export function RoleBadge({ role, size = 'md', className, showTooltip = false }: RoleBadgeProps) {
  const style = roleStyles[role] ?? roleStyles.support;
  const description = roleDescriptions[role];

  const badgeElement = (
    <Badge
      variant="outline"
      className={cn(
        'border-0 font-medium',
        style.bg,
        style.text,
        sizeStyles[size],
        className
      )}
    >
      {style.label}
    </Badge>
  );

  if (showTooltip && description) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {badgeElement}
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-xs">{description}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return badgeElement;
}
